import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import { MetricLabel } from './MetricLabel';
import type { CumulativeReturnBreakdown } from '../../types/calculations';

interface ReturnBreakdownCardProps {
  breakdown5yr: CumulativeReturnBreakdown;
  breakdown10yr: CumulativeReturnBreakdown;
  sellingCostPct?: number;
  depreciationRecapturePct?: number;
  capitalGainsPct?: number;
  stateTaxPct?: number;
  className?: string;
}

/**
 * ReturnBreakdownCard — "Where Your Money Comes From"
 *
 * 3-part cumulative return decomposition:
 *   1. Cumulative Cash Flow (pre-tax operating income)
 *   2. Cumulative Tax Benefits (depreciation shield)
 *   3. Net Sale Proceeds (with expandable deduction waterfall)
 *
 * Two columns: 5-year and 10-year side-by-side.
 * Sale waterfall collapsed by default, expands with chevron.
 */
export function ReturnBreakdownCard({
  breakdown5yr: b5,
  breakdown10yr: b10,
  sellingCostPct,
  depreciationRecapturePct,
  capitalGainsPct,
  stateTaxPct,
  className = '',
}: ReturnBreakdownCardProps) {
  const [waterfallOpen, setWaterfallOpen] = useState(false);

  return (
    <div className={`bg-charcoal border border-slate-dark rounded-lg p-5 ${className}`}>
      {/* Header */}
      <p className="text-sm uppercase tracking-[0.15em] text-ivory/60 font-semibold mb-4">
        Cumulative Return Decomposition
      </p>

      {/* Column headers */}
      <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 mb-1">
        <div />
        <span className="text-sm uppercase tracking-wider text-ivory/40 font-semibold text-right w-24">
          5-Year
        </span>
        <span className="text-sm uppercase tracking-wider text-ivory/40 font-semibold text-right w-24">
          10-Year
        </span>
      </div>

      {/* Component 1: Cumulative Cash Flow */}
      <Row
        label="Cumulative Cash Flow"
        val5={b5.cumulative_cash_flow}
        val10={b10.cumulative_cash_flow}
        colorClass="text-ivory"
      />

      {/* Component 2: Cumulative Tax Benefits */}
      <Row
        label="Cumulative Tax Benefits"
        val5={b5.cumulative_tax_benefits}
        val10={b10.cumulative_tax_benefits}
        colorClass="text-amber-300"
        prefix="+"
      />

      {/* Component 3: Net Sale Proceeds */}
      <Row
        label="Net Sale Proceeds"
        val5={b5.net_sale_proceeds}
        val10={b10.net_sale_proceeds}
        colorClass="text-emerald"
      />

      {/* Divider */}
      <div className="border-t-2 border-gold/30 my-2" />

      {/* Total Return */}
      <Row
        label="Total Return"
        val5={b5.total_return}
        val10={b10.total_return}
        colorClass="text-ivory"
        bold
      />

      {/* Equity Invested */}
      <Row
        label="Equity Invested"
        val5={b5.equity_invested}
        val10={b10.equity_invested}
        colorClass="text-ivory/60"
      />

      {/* Profit */}
      <Row
        label="Profit"
        val5={b5.profit}
        val10={b10.profit}
        colorClass="text-emerald"
        bold
      />

      {/* Equity Multiple */}
      <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 py-1.5">
        <span className="text-ivory font-semibold text-sm"><MetricLabel metric="equity_multiple">Equity Multiple</MetricLabel></span>
        <span className={`font-mono tabular-nums text-right w-24 font-bold text-lg ${
          b5.equity_multiple >= 2.0 ? 'text-gold' : 'text-ivory'
        }`}>
          {b5.equity_multiple.toFixed(2)}x
        </span>
        <span className={`font-mono tabular-nums text-right w-24 font-bold text-lg ${
          b10.equity_multiple >= 2.0 ? 'text-gold' : 'text-ivory'
        }`}>
          {b10.equity_multiple.toFixed(2)}x
        </span>
      </div>

      {/* Expandable Sale Waterfall */}
      <div className="mt-3">
        <button
          onClick={() => setWaterfallOpen(!waterfallOpen)}
          className="flex items-center gap-2 text-sm uppercase tracking-wider text-ivory/40 hover:text-ivory/70 transition-colors cursor-pointer"
        >
          {waterfallOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          Sale Proceeds Waterfall
        </button>

        {waterfallOpen && (
          <div className="mt-2 bg-obsidian/50 border border-white/5 rounded-lg p-4 space-y-0.5">
            <Row
              label="Gross Sale Price"
              val5={b5.sale_price}
              val10={b10.sale_price}
              colorClass="text-ivory/80"
              small
            />
            <Row
              label={`Less: Selling Costs (${((sellingCostPct ?? 0.05) * 100).toFixed(0)}%)`}
              val5={-b5.selling_costs}
              val10={-b10.selling_costs}
              colorClass="text-crimson/80"
              small
            />
            <Row
              label="Less: Loan Payoff"
              val5={-b5.loan_payoff}
              val10={-b10.loan_payoff}
              colorClass="text-crimson/80"
              small
            />
            <div className="border-t border-white/10 my-1.5" />
            <Row
              label={`Less: Depr Recapture (${((depreciationRecapturePct ?? 0.25) * 100).toFixed(0)}%)`}
              val5={-b5.depreciation_recapture_tax}
              val10={-b10.depreciation_recapture_tax}
              colorClass="text-crimson/80"
              small
            />
            <Row
              label={`Less: Capital Gains (${((capitalGainsPct ?? 0.238) * 100).toFixed(1)}%)`}
              val5={-b5.capital_gains_tax}
              val10={-b10.capital_gains_tax}
              colorClass="text-crimson/80"
              small
            />
            <Row
              label={`Less: State Tax (${((stateTaxPct ?? 0.0307) * 100).toFixed(2)}%)`}
              val5={-b5.state_tax}
              val10={-b10.state_tax}
              colorClass="text-crimson/80"
              small
            />
            <div className="border-t border-white/10 my-1.5" />
            <Row
              label="Net After-Tax Proceeds"
              val5={b5.net_sale_proceeds}
              val10={b10.net_sale_proceeds}
              colorClass="text-emerald"
              small
              bold
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Reusable row for the 3-column grid. */
function Row({
  label,
  val5,
  val10,
  colorClass,
  bold = false,
  small = false,
  prefix = '',
}: {
  label: string;
  val5: number;
  val10: number;
  colorClass: string;
  bold?: boolean;
  small?: boolean;
  prefix?: string;
}) {
  const textSize = small ? 'text-sm' : 'text-sm';
  const fontWeight = bold ? 'font-semibold' : '';

  return (
    <div className={`grid grid-cols-[1fr_auto_auto] gap-x-4 py-1 ${textSize}`}>
      <span className={`text-ivory/70 ${fontWeight}`}>{label}</span>
      <span className={`font-mono tabular-nums text-right w-24 ${colorClass} ${fontWeight}`}>
        {prefix}{formatCurrency(val5)}
      </span>
      <span className={`font-mono tabular-nums text-right w-24 ${colorClass} ${fontWeight}`}>
        {prefix}{formatCurrency(val10)}
      </span>
    </div>
  );
}
